import { OptionalMark } from "@patient-watch/forms";
import { useParams } from "@tanstack/react-router";
import { Save, Trash2 } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { Input } from "@patient-watch/ui/input";
import { Slider } from "@patient-watch/ui/slider";
import { useBaselineEntryValueByInputIdAndTrackerId } from "@/features/entry-values/api/selectBaselineEntryValueByInputIdAndTrackerId";
import { buildFollowUpInitialValue } from "@/features/entry-values/utils/msAdlRatingsListHelpers";
import { useUpdateAllRatingListKeys } from "@/features/entry-values/api/updateAllRatingListKeys";
import { useRatingsListCopy } from "@/features/i18n/ratingsListCopy";
import type { InputT } from "@/types";

interface ActivityRatingsProps {
    input: InputT;
    name: string;
    label?: string;
    description?: string;
}

const RatingsList: React.FC<ActivityRatingsProps> = ({ input, name, label, description }) => {
    const { watch, setValue } = useFormContext();
    const copy = useRatingsListCopy();
    const [newActivity, setNewActivity] = useState("");
    const [editingActivities, setEditingActivities] = useState<Record<string, string>>({});
    const [hasInitialized, setHasInitialized] = useState(false);
    const [focusedActivity, setFocusedActivity] = useState<string | null>(null);

    const { trackerId, entryId } = useParams({ strict: false });

    const {
        data: baselineState,
        isLoading,
        isError,
    } = useBaselineEntryValueByInputIdAndTrackerId(input.input_id, trackerId, entryId);

    const updateAllKeysMutation = useUpdateAllRatingListKeys();

    const scaleOptions = input.scale_options as Array<{
        min?: number;
        max?: number;
        step?: number;
        maxActivities?: number;
    }> | null;
    const min = scaleOptions?.[0]?.min ?? 0;
    const max = scaleOptions?.[0]?.max ?? 10;
    const step = scaleOptions?.[0]?.step ?? 1;
    const maxActivities = scaleOptions?.[0]?.maxActivities ?? 5;

    const currentValue = (watch(name) as Record<string, number>) || {};
    const isFollowUp = baselineState?.isFollowUp ?? false;
    const lockedActivities = baselineState?.lockedActivities ?? [];
    const displayActivities = isFollowUp ? lockedActivities : Object.keys(currentValue);

    useEffect(() => {
        if (hasInitialized || isLoading || !baselineState) return;

        if (Object.keys(currentValue).length > 0) {
            setHasInitialized(true);
            return;
        }

        if (isFollowUp) {
            setValue(name, buildFollowUpInitialValue(lockedActivities, min));
        }

        setHasInitialized(true);
    }, [baselineState, currentValue, hasInitialized, isFollowUp, isLoading, lockedActivities, min, name, setValue]);

    const addActivity = (activity: string) => {
        if (isFollowUp) return;
        if (Object.keys(currentValue).length < maxActivities && currentValue[activity] === undefined) {
            setValue(name, {
                ...currentValue,
                [activity]: 5,
            });
        }
    };

    const removeActivity = (activity: string) => {
        if (isFollowUp) return;
        const updatedValue = { ...currentValue };
        delete updatedValue[activity];
        setValue(name, updatedValue);
    };

    const updateRating = (activity: string, rating: number) => {
        if (isFollowUp && !lockedActivities.includes(activity)) return;
        setValue(name, {
            ...currentValue,
            [activity]: rating,
        });
    };

    const addCustomActivity = () => {
        if (isFollowUp) return;
        if (newActivity.trim() && Object.keys(currentValue).length < maxActivities) {
            addActivity(newActivity.trim());
            setNewActivity("");
        }
    };

    if (isLoading) {
        return (
            <section className="pt-4">
                <p className="text-muted-foreground text-lg">{copy.loading}</p>
            </section>
        );
    }

    if (isError) {
        return (
            <section className="pt-4">
                <p className="text-destructive text-lg">{copy.loadError}</p>
            </section>
        );
    }

    if (isFollowUp && lockedActivities.length === 0) {
        return (
            <section className="pt-4">
                <p className="text-destructive text-lg">{copy.noBaselineActivities}</p>
            </section>
        );
    }

    return (
        <section className="pt-4" data-testid="ratings-list">
            <div className="space-y-6">
                <div>
                    {label && (
                        <ui.Label className="font-semibold text-lg">
                            {label}
                            {input.is_optional && <OptionalMark />}
                        </ui.Label>
                    )}
                    {description && <p className="mt-1 text-muted-foreground text-lg">{description}</p>}
                    {isFollowUp && <p className="mt-2 text-muted-foreground text-lg">{copy.followUpHint}</p>}
                </div>

                {!isFollowUp && Object.keys(currentValue).length < maxActivities && (
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <Input
                                data-testid="ratings-list-add-activity-input"
                                placeholder={copy.addActivityPlaceholder(
                                    Object.keys(currentValue).length + 1,
                                    maxActivities
                                )}
                                value={newActivity}
                                onChange={(e) => setNewActivity(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCustomActivity();
                                    }
                                }}
                            />
                            <Button
                                type="button"
                                variant="default"
                                size="md"
                                data-testid="ratings-list-add-activity-button"
                                onClick={addCustomActivity}
                                disabled={!newActivity.trim()}
                            >
                                {copy.add}
                            </Button>
                        </div>
                    </div>
                )}

                {displayActivities.map((activity) => {
                    const rating = currentValue[activity] ?? min;

                    return (
                        <div
                            key={activity}
                            className="space-y-3 bg-white p-4 border border-gray-200 rounded-lg"
                            data-testid="ratings-list-activity"
                            data-activity={activity}
                        >
                            <div className="flex justify-between items-center gap-2">
                                {isFollowUp ? (
                                    <p
                                        className="flex-1 font-medium text-lg"
                                        data-testid="ratings-list-locked-activity-name"
                                    >
                                        {activity}
                                    </p>
                                ) : (
                                    <div className="flex flex-1 items-center gap-2">
                                        <Input
                                            value={editingActivities[activity] ?? activity}
                                            onChange={(e) => {
                                                setEditingActivities((prev) => ({
                                                    ...prev,
                                                    [activity]: e.target.value,
                                                }));
                                            }}
                                            onFocus={() => setFocusedActivity(activity)}
                                            onBlur={() => setFocusedActivity(null)}
                                            className="flex-1 bg-transparent focus:bg-white p-0 px-2 focus:border border-0 focus:border-gray-300 font-medium text-lg"
                                            data-testid="ratings-list-editable-activity-name"
                                        />
                                        {(focusedActivity === activity || editingActivities[activity]) && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => {
                                                    const newName = editingActivities[activity];
                                                    if (newName?.trim() && newName !== activity) {
                                                        const updatedValue: Record<string, number> = {};
                                                        Object.entries(currentValue).forEach(([key, value]) => {
                                                            if (key === activity) {
                                                                updatedValue[newName.trim()] = value as number;
                                                            } else {
                                                                updatedValue[key] = value as number;
                                                            }
                                                        });
                                                        setValue(name, updatedValue);

                                                        updateAllKeysMutation.mutate({
                                                            trackerId,
                                                            inputId: input.input_id,
                                                            oldKey: activity,
                                                            newKey: newName.trim(),
                                                        });

                                                        setEditingActivities((prev) => {
                                                            const newState = { ...prev };
                                                            delete newState[activity];
                                                            return newState;
                                                        });
                                                    }
                                                }}
                                                disabled={
                                                    !editingActivities[activity]?.trim() ||
                                                    editingActivities[activity] === activity
                                                }
                                            >
                                                <Save size={20} />
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {!isFollowUp && (
                                    <Button
                                        type="button"
                                        variant="destructive-ghost"
                                        size="sm"
                                        data-testid="ratings-list-remove-activity"
                                        onClick={() => removeActivity(activity)}
                                    >
                                        <Trash2 size={20} />
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-center">
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            min={min}
                                            max={max}
                                            value={rating}
                                            onChange={(e) => {
                                                const newRating = parseInt(e.target.value) || min;
                                                const clampedRating = Math.max(min, Math.min(max, newRating));
                                                updateRating(activity, clampedRating);
                                            }}
                                            className="border-gray-300 w-fit font-bold text-lg text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                        />
                                        <span className="text-gray-600 text-lg">/10</span>
                                    </div>
                                </div>

                                <div className="py-4 w-full">
                                    <Slider
                                        value={[rating]}
                                        onValueChange={([value]) => updateRating(activity, value || min)}
                                        min={min}
                                        max={max}
                                        step={step}
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <span className="w-24 text-gray-600 text-lg">{copy.noDifficulty}</span>
                                    <div className="relative flex-1"></div>
                                    <span className="w-24 text-gray-600 text-lg text-right">{copy.unableToDo}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {!isFollowUp && Object.keys(currentValue).length === 0 && (
                    <div className="py-8 text-gray-400 text-center">
                        <p className="text-lg">{copy.emptyHint}</p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default RatingsList;
